import { useMemo } from 'react';
import classNames from 'classnames';

import { Model } from '@adobe/aem-spa-page-model-manager';

import { ArrowsImage } from '../../arrows-image/arrows-image';
import { Button } from '../../button/button.component';
import { Text } from '../../text/text.component';
import { getComponentFromProps, getPropsFromModel } from '../../../utils/use-child-components';
import { useHasGradientBackground, useHasMidBlueBackground } from '../../../utils/use-has-background-class';

import { ColumnContainerText } from './column-container-text/column-container-text';
import { ColumnContainerTitle, ColumnContainerTitleType } from './column-container-title/column-container-title';
import { getTabCQItemsWithAppliedClasses } from './getTabCqItemsWithAppliedClasses';
import { ColumnContainerProps, ColumnContainerProperties } from './column-container.config';

const arrowClassNames = ['snowflake-column-container-white-arrow', 'snowflake-column-container-blue-arrow'];

export const indexingGridProps = (gridProps: ColumnContainerProperties) => {
  const mappedProps: ColumnContainerProperties = { ...gridProps };
  mappedProps?.cqItemsOrder?.forEach((key: string, index: number) => {
    mappedProps.cqItems[key].index = index;
    mappedProps.cqItems[key].isLast = index === mappedProps.cqItemsOrder.length - 1;
    mappedProps.cqItems[key].isFirst = index === 0;
  });
  return mappedProps;
};

const ColumnContainer = ({
  additionalClasses,
  appliedCssClassNames = 'snowflake-column-container-white-bg',
  button,
  cqPath,
  footnote,
  grid,
  id,
  tabs,
  text,
  title,
}: ColumnContainerProps) => {
  const gridProps = getPropsFromModel(grid, cqPath, 'grid');
  const Grid = getComponentFromProps(gridProps);
  const tabsProps = getPropsFromModel(tabs, cqPath, 'tabs');
  const Tabs = getComponentFromProps(tabsProps);

  const hasGradientBackground = useHasGradientBackground(appliedCssClassNames);
  const hasMidBlueBackground = useHasMidBlueBackground(appliedCssClassNames);

  const mappedGridProps = useMemo(() => {
    return indexingGridProps(gridProps);
  }, [gridProps]);

  const styles = useMemo(() => {
    return {
      text: {
        'snowflake-column-container-no-tab-text': !tabs,
        'snowflake-column-container-multi-no-tab-text':
          !tabs && ['three-columns', 'two-columns'].some((columnId) => id?.includes(columnId)),
        'text-color-text-04-ui-bg-03': hasGradientBackground,
        'text-color-text-04': hasMidBlueBackground,
        'text-color-text-01': !hasGradientBackground && !hasMidBlueBackground,
      },
    };
  }, [hasGradientBackground, hasMidBlueBackground, tabs, id]);

  const getMarginBottom = () => {
    if (!text && !!tabs) {
      return ColumnContainerTitleType.NONE;
    }
    if (!text) {
      return ColumnContainerTitleType.LARGE;
    }
    return undefined;
  };

  return (
    <div className={classNames('snowflake-column-container-outer', additionalClasses)}>
      <div
        className={classNames('snowflake-column-container snowflake-container', { 'no-title': !title })}
        data-testid="snowflake-column-container"
      >
        {arrowClassNames.some((arrowClassName) => appliedCssClassNames?.includes(arrowClassName)) && <ArrowsImage />}
        {title && (
          <ColumnContainerTitle
            title={title}
            hasGradientBackground={hasGradientBackground}
            hasMidBlueBackground={hasMidBlueBackground}
            marginBottom={getMarginBottom()}
          />
        )}

        {text && <ColumnContainerText className={classNames(styles.text)} textProps={text} />}
        {Tabs && getTabCQItemsWithAppliedClasses<Model>(tabsProps?.cqItems, appliedCssClassNames as string) && (
          <div className="snowflake-column-container-multi-tab" data-testid="snowflake-column-container-tabs">
            <Tabs
              {...tabsProps}
              cqItems={getTabCQItemsWithAppliedClasses(tabsProps?.cqItems, appliedCssClassNames as string)}
              appliedCssClassNames={classNames({
                'tabs-blue-background': hasGradientBackground || hasMidBlueBackground,
                'tabs-white-background': !hasGradientBackground && !hasMidBlueBackground,
              })}
            />
          </div>
        )}
        {Grid && <Grid appliedCssClassNames={appliedCssClassNames} {...mappedGridProps} />}
        {footnote && (
          <div className="snowflake-column-container-footnote" data-testid="snowflake-column-container-footnote">
            <Text {...footnote} isInEditor={false} />
          </div>
        )}
        {button && (
          <div
            data-testid="snowflake-column-container-button"
            className={classNames(
              'snowflake-column-container-button snowflake-button-regular snowflake-button-primary snowflake-button-blue w-100 d-flex justify-center',
              {
                'snowflake-button-white': hasGradientBackground || hasMidBlueBackground,
              }
            )}
          >
            <Button {...button} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnContainer;
